/**
 * profile-facet-draft — the Kompass document's LOCAL, per-user draft of the ONE section the
 * person is editing.
 *
 * The profile's edit-in-place sections (»Identität«, »Rahmenbedingungen«, »Deine Stimme«) are
 * uncontrolled forms whose `.value` is bound to server truth: the typed revision never reaches
 * a signal at all, so it is invisible to any signal-mirroring rescue. On a page death the
 * document re-renders the PRE-EDIT text and closes the editor, which reads as "my edit saved
 * and then reverted" — the same false signature as the Werte-Sektion P0. And the save click is
 * itself the trigger: `saveRevision`'s POST is exactly where a dead session 401s and redirects.
 *
 * The rescue is deliberately GENERIC over the form rather than per field. The document owns a
 * registry of asset sections (`KompassAssetSection`), each supplying only its own fields, so a
 * per-field mirror would have to be re-implemented by every asset added later — and the one
 * that forgets is silently unprotected. Serialising the whole form by control name means a new
 * section is covered the day it is registered.
 *
 * Only one section can be open at a time, so this is a single slot; a draft naming a different
 * section than the one being restored is simply the section to reopen.
 */

import { createLocalDraftStore, type LocalDraftStore } from "./local-draft"

/** One control's value: text for inputs/textareas, a list for a checkbox group. */
export type FacetFieldValue = string | string[]

/** The persisted editor state: which section, and every named control in its form. */
export interface ProfileFacetDraft {
  /** The section id being edited (`identitaet` | `rahmen` | `stimme` | a later asset's id). */
  section: string
  /** Every named control in the open form, by control name. */
  fields: Record<string, FacetFieldValue>
  /** When this draft was last touched (ISO-8601). */
  updatedAt: string
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null

const isFieldValue = (v: unknown): v is FacetFieldValue =>
  typeof v === "string" || (Array.isArray(v) && v.every((x) => typeof x === "string"))

/**
 * A total, dependency-free guard. Structural only: the section id and the control names are
 * matched against the live registry by the surface, and the writer endpoints re-validate the
 * revision server-side.
 */
export function isProfileFacetDraft(v: unknown): v is ProfileFacetDraft {
  if (!isRecord(v)) return false
  const fields = v["fields"]
  return (
    typeof v["section"] === "string" &&
    v["section"].length > 0 &&
    isRecord(fields) &&
    Object.values(fields).every(isFieldValue) &&
    typeof v["updatedAt"] === "string"
  )
}

export type ProfileFacetDraftStore = LocalDraftStore<ProfileFacetDraft>

const KEY_PREFIX = "career:profile-facet:draft:v1:"

/** A per-user facet-draft store, guarded by {@link isProfileFacetDraft}. */
export function createProfileFacetDraftStore(userId: string | null, explicitStorage?: Storage): ProfileFacetDraftStore {
  return createLocalDraftStore(KEY_PREFIX, userId, isProfileFacetDraft, explicitStorage)
}

/**
 * Snapshot every named control in a form.
 *
 * Checkboxes collapse into one list per name (the work-mode group is one field with several
 * boxes), so an all-unchecked group is recorded as `[]` — a real, restorable answer, not an
 * absence. Buttons carry no answer and are skipped.
 */
export function serializeForm(form: HTMLFormElement): Record<string, FacetFieldValue> {
  const fields: Record<string, FacetFieldValue> = {}
  for (const el of form.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>("[name]")) {
    const { name } = el
    if (name.length === 0) continue
    if (el instanceof HTMLInputElement && el.type === "checkbox") {
      const current = fields[name]
      const list = Array.isArray(current) ? current : []
      fields[name] = el.checked ? [...list, el.value] : list
      continue
    }
    if (el instanceof HTMLInputElement && (el.type === "button" || el.type === "submit")) continue
    fields[name] = el.value
  }
  return fields
}

/** One rescued text field, or the server's value when this control was never touched. */
export function draftText(fields: Record<string, FacetFieldValue> | null, name: string, serverValue: string): string {
  const value = fields?.[name]
  return typeof value === "string" ? value : serverValue
}

/** One rescued checkbox group, or the server's list when this control was never touched. */
export function draftList(fields: Record<string, FacetFieldValue> | null, name: string, serverValue: readonly string[]): readonly string[] {
  const value = fields?.[name]
  return Array.isArray(value) ? value : serverValue
}
